import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ItalyMap from "../components/ItalyMap";
import MyPals from "../components/MyPals";
import WelcomeModal from "../components/WelcomeModal";
import Footer from "../components/Footer";
import LanguageSelector from "../components/LanguageSelector";
import { getAllGearpals } from "../backend/api/gearlpal";
import { useQuery } from "@tanstack/react-query";
import { getAllLivepals } from "../backend/api/livepal";
import toast from "react-hot-toast";
import { useUserStore } from "../stores/UserStore";
import { useLoginAndRegisterStore } from "../stores/LoginAndRegisterStore";
import i18n from "../i18n";
import AddOrEditPalForm from "../components/AddOrEditPalForm/AddOrEditPalForm";
import { useFiltersStore } from "../stores/FiltersStore";
import { useWelcome } from "../components/providers/WelcomeProvider";
import { NavBar } from "../components/navBar";
import NoContactAlert from "../components/NoContactAlert";

const Home = () => {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const { user, logout } = useUserStore();
  const { isRegisterOpen, openRegister, openLogin, close } =
    useLoginAndRegisterStore();
  const [palTypeToAdd, setPalTypeToAdd] = useState<
    "LivePal" | "GearPal" | null
  >(null);

  const { isOpen: showWelcome, close: closeWelcome } = useWelcome();

  const {
    selectedTypes,
    zipCode,
    searchRadius,
    selectedCategory,
    selectedSubcategory,
    selectedType,
  } = useFiltersStore();

  const isGearPalOnly =
    selectedTypes.length === 1 && selectedTypes.includes("GearPal");
  const isLivePalOnly =
    selectedTypes.length === 1 && selectedTypes.includes("LivePal");

  const {
    data: gearpals,
    isLoading: isLoadingGearpals,
    isError: isErrorGearpals,
  } = useQuery({
    queryKey: [
      "gearpals",
      i18n.language,
      selectedTypes,
      zipCode,
      searchRadius,
      selectedCategory,
      selectedSubcategory,
      selectedType,
    ],
    queryFn: () =>
      getAllGearpals(i18n.language, {
        types: selectedTypes,
        zipCode,
        searchRadius,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        type: selectedType,
        off: isLivePalOnly,
      }),
  });

  const {
    data: livepals,
    isLoading: isLoadingLivepals,
    isError: isErrorLivepals,
  } = useQuery({
    queryKey: ["livepals", i18n.language, selectedTypes, zipCode, searchRadius],
    queryFn: () =>
      getAllLivepals(i18n.language, {
        types: selectedTypes,
        zipCode,
        searchRadius,
        off: isGearPalOnly,
      }),
  });

  useEffect(() => {
    if (isErrorGearpals) {
      toast.error(
        t("errors.gearpalsLoadFailed", "Errore nel caricamento dei GearPals")
      );
    }
  }, [isErrorGearpals, t]);

  useEffect(() => {
    if (isErrorLivepals) {
      toast.error(
        t("errors.livepalsLoadFailed", "Errore nel caricamento dei LivePals")
      );
    }
  }, [isErrorLivepals, t]);

  const handleAddVenue = () => {
    if (user) {
      setPalTypeToAdd("LivePal");
      setIsFormOpen(true);
    } else {
      openLogin();
    }
  };

  const handleAddGear = () => {
    if (user) {
      setPalTypeToAdd("GearPal");
      setIsFormOpen(true);
    } else {
      openLogin();
    }
  };

  const handleCloseWelcome = () => {
    closeWelcome();
  };

  return (
    <div className="relative" style={{
      height: "100dvh",
    }}>
      <NavBar />
      <NoContactAlert />

      <ItalyMap
        livePals={Array.isArray(livepals) ? livepals : []}
        gearPals={Array.isArray(gearpals) ? gearpals : []}
        onPalUpdate={() => setLastUpdate(Date.now())}
      />

      {showWelcome && <WelcomeModal onClose={handleCloseWelcome} />}

      {user && (
        <>
          {user && (
            <AddOrEditPalForm
              isOpen={isFormOpen}
              onClose={() => {
                setIsFormOpen(false);
                setPalTypeToAdd(null);
              }}
              onSubmitSuccess={() => {
                setIsFormOpen(false);
                setPalTypeToAdd(null);
                setLastUpdate(Date.now());
              }}
              palType={palTypeToAdd}
            />
          )}
        </>
      )}

      {user && (
        <>
          {user && (
            <MyPals
              gearPals={gearpals || []}
              currentUser={user.email}
              onAddVenue={handleAddVenue}
              onAddGear={handleAddGear}
            />
          )}
        </>
      )}

      <Footer />
    </div>
  );
};

export default Home;
